import { Box } from '@mui/material';
import React from 'react';

export const PURPLE_PALETTE = [
  '#7c3aed',
  '#8b5cf6',
  '#a855f7',
  '#c084fc',
  '#d946ef',
  '#e879f9',
];

export const GREEN_PALETTE = [
  '#059669',
  '#10b981',
  '#22c55e',
  '#34d399',
  '#4ade80',
  '#86efac',
];

const ParticleItem = ({ xPct, yPct, tx, ty, size, color, delay, duration }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current) return;
    ref.current.animate(
      [
        {
          transform: 'translate(-50%, -50%) scale(0)',
          opacity: 1,
          offset: 0,
        },
        {
          transform: `translate(calc(-50% + ${tx * 0.6}px), calc(-50% + ${ty * 0.6}px)) scale(1)`,
          opacity: 1,
          offset: 0.45,
        },
        {
          transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0.4)`,
          opacity: 0,
          offset: 1,
        },
      ],
      { duration, delay, easing: 'ease-out', fill: 'forwards' },
    );
  }, [tx, ty, delay, duration]);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        top: `${yPct}%`,
        left: `${xPct}%`,
        pointerEvents: 'none',
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        opacity: 0,
      }}
    />
  );
};

const radialParticles = (count, colors, distanceScale) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    const distance = (55 + Math.random() * 35) * distanceScale;
    return {
      xPct: 50,
      yPct: 50,
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance,
      size: 5 + Math.random() * 5,
      color: colors[i % colors.length],
      delay: Math.random() * 60,
    };
  });

const stripParticles = (count, colors, distanceScale) =>
  Array.from({ length: count }, (_, i) => {
    const xPct = ((i + 0.5) / count) * 100 + (Math.random() - 0.5) * 8;
    return {
      xPct,
      yPct: 50,
      tx: (Math.random() - 0.5) * 40 * distanceScale,
      ty: -(45 + Math.random() * 55) * distanceScale,
      size: 5 + Math.random() * 5,
      color: colors[i % colors.length],
      delay: Math.random() * 200,
    };
  });

const ParticleBurst = ({
  pattern = 'radial',
  count,
  colors = PURPLE_PALETTE,
  duration = 700,
  distanceScale = 1,
}) => {
  const resolvedCount = count ?? (pattern === 'strip' ? 22 : 14);
  const particles = React.useMemo(
    () =>
      pattern === 'strip'
        ? stripParticles(resolvedCount, colors, distanceScale)
        : radialParticles(resolvedCount, colors, distanceScale),
    [pattern, resolvedCount, colors, distanceScale],
  );
  return (
    <>
      {particles.map((p, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <ParticleItem key={i} {...p} duration={duration} />
      ))}
    </>
  );
};

export default ParticleBurst;
